"use client";
import { useEffect, useRef, useState } from "react";
import { analyticsLegalHref, analyticsConsent, analyticsConsentChanged, anonymousAuthState, captureAnonymousView, chooseAnalyticsConsent, clearAnalyticsIdentity, publicViewDimensions, resolveAnonymousConfig, resolveCookieScope, type AnalyticsAuthState, type AnalyticsSurface, type CookieScope } from "./anonymousAnalytics";
export type AnonymousAnalyticsSettings = {
    projectId?: string;
    projectMode?: string;
    productionBindingApproved?: string;
    oldTokenRevoked?: string;
    nonProductionRetired?: string;
    landingUrl?: string;
    enabled?: string;
    environment?: string;
    host?: string;
    projectToken?: string;
    deploySha?: string;
    approvedHosts?: string;
    approvedParentDomain?: string;
    trustApproved?: string;
    identityMaxAgeSeconds?: string;
    identityPolicyApproved?: string;
};
function scope(settings: AnonymousAnalyticsSettings): CookieScope | null { return typeof window === "undefined" ? null : resolveCookieScope({ ...settings, hostname: window.location.hostname, protocol: window.location.protocol }); }
function optionalStorage(): Storage | undefined {
    try { return window.localStorage; } catch { return undefined; }
}
function listen(change: () => void) {
    const events = [analyticsConsentChanged, "focus", "storage", "pageshow"];
    for (const event of events) window.addEventListener(event, change);
    document.addEventListener("visibilitychange", change);
    // A cookie shared across origins has no cross-origin storage event. This
    // local-only observer detects withdrawal while the page remains focused;
    // network retries are separately capped at three with backoff.
    const timer = setInterval(change, 1000);
    return () => {
        for (const event of events) window.removeEventListener(event, change);
        document.removeEventListener("visibilitychange", change);
        clearInterval(timer);
    };
}
export function AnonymousPageView({ settings, surface, path, authState, publicEligible = true }: {
    settings: AnonymousAnalyticsSettings;
    surface: AnalyticsSurface;
    path: string;
    authState: AnalyticsAuthState;
    publicEligible?: boolean;
}) {
    const auth = useRef(authState);
    auth.current = authState;
    const logical = useRef<{
        path: string;
        id: string;
        sent: boolean;
        attempts: number;
        retryAt: number;
    } | null>(null);
    useEffect(() => {
        const cookieScope = scope(settings);
        if (!cookieScope)
            return;
        const config = resolveAnonymousConfig(settings, cookieScope);
        const dimensions = publicViewDimensions(surface, path, publicEligible);
        if (!logical.current || logical.current.path !== path)
            logical.current = { path, id: `av2:${crypto.randomUUID()}`, sent: false, attempts: 0, retryAt: 0 };
        const view = logical.current;
        let controller: AbortController | null = null;
        let active = true;
        let sending = false;
        const send = async () => {
            const consent = analyticsConsent(document.cookie, cookieScope.environment);
            if (consent !== "granted" || anonymousAuthState(auth.current, document.cookie) !== "anonymous") {
                controller?.abort();
                if (consent !== "granted")
                    clearAnalyticsIdentity(cookieScope, { write: v => { document.cookie = v; }, storage: optionalStorage() });
                return;
            }
            if (!active || sending || view.sent || view.attempts >= 3 || Date.now() < view.retryAt || !config || !dimensions)
                return;
            sending = true;
            controller = new AbortController();
            view.attempts++;
            view.retryAt = Date.now() + 10000 * view.attempts;
            const sent = await captureAnonymousView({ config, view: { ...dimensions, insertId: view.id }, auth: () => anonymousAuthState(auth.current, document.cookie), signal: controller.signal, browser: { read: () => document.cookie, write: v => { document.cookie = v; }, hostname: window.location.hostname, crypto, storage: optionalStorage(), pause: ms => new Promise(r => setTimeout(r, ms)) } });
            if (active && sent)
                view.sent = true;
            sending = false;
        };
        void send();
        const unsubscribe = listen(() => { void send(); });
        return () => { active = false; controller?.abort(); unsubscribe(); };
    }, [settings, surface, path, authState, publicEligible]);
    return null;
}
const copy = { en: { cookiePolicy: "Cookie Policy", privacyPolicy: "Privacy Policy", title: "Your analytics choice", text: "Optional analytics help us understand which public pages are useful. They stay off unless you accept. Your browser identifier is separate from your account and free AI allowance.", choices: "Privacy choices", reject: "Reject optional analytics", withdraw: "Withdraw optional analytics", accept: "Accept optional analytics" }, es: { cookiePolicy: "Política de cookies", privacyPolicy: "Política de privacidad", title: "Tu elección de analítica", text: "La analítica opcional nos ayuda a saber qué páginas públicas son útiles. Solo se activa si aceptas. El identificador del navegador es independiente de tu cuenta y de tus preguntas de IA gratuitas.", choices: "Opciones de privacidad", reject: "Rechazar analítica opcional", withdraw: "Retirar analítica opcional", accept: "Aceptar analítica opcional" }, "pt-BR": { cookiePolicy: "Política de cookies", privacyPolicy: "Política de privacidade", title: "Sua escolha de análise", text: "A análise opcional ajuda a entender quais páginas públicas são úteis. Ela só é ativada se você aceitar. O identificador do navegador é separado da sua conta e das suas perguntas de IA gratuitas.", choices: "Opções de privacidade", reject: "Rejeitar análise opcional", withdraw: "Retirar análise opcional", accept: "Aceitar análise opcional" } };
export function AnonymousConsentControls({ settings, locale = "en" }: {
    settings: AnonymousAnalyticsSettings;
    locale?: string;
}) {
    const [consent, setConsent] = useState<"granted" | "denied" | "unset" | "loading">("loading");
    const [open, setOpen] = useState(false);
    const [available, setAvailable] = useState(false);
    useEffect(() => { const s = scope(settings); setAvailable(Boolean(s)); if (!s)
        return; const sync = () => { const c = analyticsConsent(document.cookie, s.environment); setConsent(c); if (c !== "granted")
        clearAnalyticsIdentity(s, { write: v => { document.cookie = v; }, storage: optionalStorage() }); }; sync(); return listen(sync); }, [settings]);
    if (!available || consent === "loading")
        return null;
    const t = copy[locale as keyof typeof copy] ?? copy.en;
    const choose = (choice: "granted" | "denied") => {
        const s = scope(settings);
        if (!s)
            return;
        chooseAnalyticsConsent(choice, s, { write: v => { document.cookie = v; }, storage: optionalStorage() });
        // Existing authenticated/Vercel producers retain their own approved contract.
        // Preserve withdrawal there too; no blind parent-domain grant is introduced.
        document.cookie = `dronejob_analytics_consent=${choice}; Path=/; Max-Age=15552000; SameSite=Lax${s.secure ? "; Secure" : ""}`;
        setOpen(false);
        setConsent(analyticsConsent(document.cookie, s.environment));
        window.dispatchEvent(new Event(analyticsConsentChanged));
        try {
            optionalStorage()?.setItem(`dj:analytics-consent-notification:${s.environment}`, String(Date.now()));
        }
        catch { }
    };
    const button = { border: "1px solid #64748b", borderRadius: 8, padding: "10px 16px", font: "inherit", cursor: "pointer", background: "white", color: "#0f172a" };
    return consent !== "unset" && !open ? <button type="button" style={{ ...button, position: "fixed", bottom: 16, left: 16, zIndex: 90 }} onClick={() => setOpen(true)}>{t.choices}</button> : <section aria-label={t.title} style={{ position: "fixed", bottom: 16, left: 16, right: 16, zIndex: 90, maxWidth: 800, margin: "0 auto", padding: 20, border: "1px solid #cbd5e1", borderRadius: 16, background: "white", color: "#0f172a", boxShadow: "0 8px 24px #0003" }}><h2 style={{ margin: 0, fontSize: 20 }}>{t.title}</h2><p>{t.text}</p><p><a href={analyticsLegalHref(settings.landingUrl, locale, "cookie-policy")}>{t.cookiePolicy}</a>{" · "}<a href={analyticsLegalHref(settings.landingUrl, locale, "privacy")}>{t.privacyPolicy}</a></p><div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}><button type="button" style={button} onClick={() => choose("denied")}>{consent === "granted" ? t.withdraw : t.reject}</button><button type="button" style={button} onClick={() => choose("granted")}>{t.accept}</button></div></section>;
}
